import React, {useEffect, useState} from "react";
import {useNavigate} from "react-router-dom";
import "../../assets/css/Ticket.css";
import Loading from "../Loading";
import axios from "axios";
import {API_BASE_URL} from "../../config";

const TicketsTable = ({tickets}) => {
    const navigate = useNavigate();
    const [sortConfig, setSortConfig] = useState({key: null, direction: 'ascending'});
    const [searchTerm, setSearchTerm] = useState("");
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        setLoading(true);
        axios
            .post(`${API_BASE_URL}/get_user_name`, {ids: tickets})
            .then(() => setLoading(false))
            .catch((error) => console.error("Error fetching ticket:", error));
    }, [tickets]);

    const handleRowClick = (ticketId) => {
        navigate(`/ticket/${ticketId}`);
    };

    const handleSort = (key) => {
        const sortableKeys = ['ticket_type', 'comments_count', 'assigned', 'status', 'created_at', 'time_elapsed', 'customer_name1'];
        if (!sortableKeys.includes(key)) return;

        let direction = 'ascending';
        if (sortConfig.key === key && sortConfig.direction === 'ascending') {
            direction = 'descending';
        }
        setSortConfig({key, direction});
    };

    const handleSearchChange = (e) => {
        setSearchTerm(e.target.value);
    };

    const removeAccents = (str) => str.normalize("NFD").replace(/[\u0300-\u036f]/g, "");

    const getTimeElapsedMs = (ticket) => {
        if (!ticket.created_at) return 0;
        const start = new Date(ticket.created_at);
        const end = ticket.resolved_at && ticket.resolved_at !== "0000-00-00 00:00:00" ? new Date(ticket.resolved_at) : new Date();
        return end - start;
    };

    const enrichedTickets = tickets.map(ticket => ({
        ...ticket, time_elapsed_ms: getTimeElapsedMs(ticket)
    }));

    const sortedAndFilteredTickets = React.useMemo(() => {
        // 1) filter by search term
        let filtered = enrichedTickets.filter(ticket => {
            const term = removeAccents(searchTerm).toLowerCase();
            const fields = [ticket.ticket_type, ticket.alias, ticket.title, ticket.priority_level, ticket.status, ticket.customer_email, `${ticket.name_1 || ""} ${ticket.name_2 || ""}`.trim(), ticket.created_at ? new Date(ticket.created_at).toLocaleString() : ""].map(f => removeAccents(f || "").toLowerCase());

            // also check assigned usernames
            const assignedNames = ticket.assigned_usernames_and_colors
                ?.map(u => removeAccents(u.username).toLowerCase())
                .join(" ") || "";

            return fields.join(" ").includes(term) || assignedNames.includes(term);
        });

        // 2) sort: Réouvert → warning (unassigned/ND) → sorted columns → original
        filtered.sort((a, b) => {
            // a) Réouvert first
            const reopenedA = a.status === "Réouvert";
            const reopenedB = b.status === "Réouvert";
            if (reopenedA && !reopenedB) return -1;
            if (!reopenedA && reopenedB) return 1;

            // b) warning (unassigned/ND) next
            const warningA = a.ticket_type_id === null || a.alias === "ND" || a.assigned_user === "";
            const warningB = b.ticket_type_id === null || b.alias === "ND" || b.assigned_user === "";
            if (warningA && !warningB) return -1;
            if (!warningA && warningB) return 1;

            // c) then by sortConfig if any
            if (sortConfig.key) {
                let aVal = a[sortConfig.key];
                let bVal = b[sortConfig.key];

                // special cases:
                if (sortConfig.key === 'assigned') {
                    const names = t => t.assigned_usernames_and_colors
                        ?.map(u => removeAccents(u.username).toLowerCase())
                        .sort().join(', ') || "";
                    aVal = names(a);
                    bVal = names(b);
                }
                if (sortConfig.key === 'customer_name1') {
                    const name = t => removeAccents(`${t.name_1 || ""} ${t.name_2 || ""}`.trim()).toLowerCase();
                    aVal = name(a);
                    bVal = name(b);
                }
                if (sortConfig.key === 'time_elapsed') {
                    aVal = a.time_elapsed_ms;
                    bVal = b.time_elapsed_ms;
                }
                if (sortConfig.key === 'comments_count') {
                    aVal = a.comments_count;
                    bVal = b.comments_count;
                }

                if (aVal < bVal) return sortConfig.direction === 'ascending' ? -1 : 1;
                if (aVal > bVal) return sortConfig.direction === 'ascending' ? 1 : -1;
            }

            // d) otherwise keep current order
            return 0;
        });

        return filtered;
    }, [enrichedTickets, sortConfig, searchTerm]);

    return (<div className="ticket-table-container">
        <div className="ticket-table-search">
            <input
                type="text"
                placeholder="Rechercher un ticket..."
                value={searchTerm}
                onChange={handleSearchChange}
                className="ticket-table-search-input"
            />
        </div>

        <span>{sortedAndFilteredTickets.length} tickets trouvés</span>

        <div className="ticket-table-wrapper">
            {loading ? <Loading/> : (
                <table className="ticket-table">
                <thead>
                <tr>
                    <th onClick={() => handleSort('ticket_type')}>Résumé</th>
                    <th onClick={() => handleSort('comments_count')}>Réponses</th>
                    <th onClick={() => handleSort('assigned')}>Assigné</th>
                    <th onClick={() => handleSort('status')}>État</th>
                    <th onClick={() => handleSort('created_at')}>Création</th>
                    <th onClick={() => handleSort('time_elapsed')}>Durée</th>
                </tr>
                </thead>
                <tbody>
                {sortedAndFilteredTickets.map(ticket => (<tr
                    key={ticket.ticket_id}
                    onClick={() => handleRowClick(ticket.ticket_id)}
                    className={`ticket-table-row
                       ${(ticket.status === "Réouvert" || ticket.ticket_type_id === null || ticket.alias === "ND" || ticket.assigned_user === "") ? " ticket-table-row-warning" : ""}
                       ${ticket.status === "Clôturé" ? " ticket-cloture" : ""}`}
                >
                    <td>
                        <div className="display-flex flex-direction-column gap-10">
                        <span>
                          <strong>
                            <span className="color-third">
                              {ticket.name_1 && ticket.name_2 ? `${ticket.name_1} ${ticket.name_2}` : ticket.name_1 || ticket.name_2}
                            </span>
                          </strong>
                        </span>
                            <span>
                          <strong>{ticket.ticket_type || ticket.title || "Non renseigné"}</strong>
                        </span>
                            <div style={{display: 'flex', alignItems: 'center', gap: 10}}>
                          <span
                              className="ticket-table-service text-black"
                              style={{backgroundColor: ticket.color}}
                          >
                            {ticket.alias}
                          </span>
                                <span
                                    className="ticket-table-priority"
                                    style={{
                                        backgroundColor: ticket.priority_color || "#e5e7eb",
                                        textTransform: "uppercase",
                                        fontWeight: 700
                                    }}
                                >
                            {ticket.priority_level || "Normal"}
                          </span>
                                <span style={{marginLeft: '10px'}}>
                            <em>{ticket.customer_email || ticket.mail}</em>
                          </span>
                            </div>
                        </div>
                    </td>

                    <td><span style={{padding: "10 20", fontSize: ".75rem", backgroundColor: "lightgreen", border: "solid 2px green", display: "flex", justifyContent: "center", alignItems: "center", color:"black", height: 20, width: 20, borderRadius: 50}}>{console.log(ticket)}{ticket.comments_count}</span></td>
                    <td className="ticket-table-assigned">
                        {/* Affiche une seule fois chaque utilisateur, avant ça pouvait buguer */}
                        {ticket.assigned_usernames_and_colors
                            ?.filter(
                                (user, idx, all) =>
                                    all.findIndex(u => u.username === user.username) === idx
                            )
                            .map((user, i) => (
                                <span
                                    key={i}
                                    className="ticket-table-assigned-tag"
                                    style={{color: user.color, borderColor: user.color}}
                                >
                                            {user.username}
                                          </span>
                            ))}
                    </td>
                    <td>
                      <span
                          className="ticket-table-status"
                          style={{
                              marginLeft: '10px',
                              backgroundColor: ticket.status_color || "#1a1f2c",
                              color: ticket.status_text_color || "#fff"
                          }}
                      >
                        {ticket.status}
                      </span>
                    </td>
                    <td>

                        {new Date(ticket.created_at).toLocaleString()}

                    </td>
                    <td className="ticket-table-duration">
                        {ticket.created_at ? (ticket.resolved_at && ticket.resolved_at !== "0000-00-00 00:00:00" ? (() => {
                            const diffMs = new Date(ticket.resolved_at) - new Date(ticket.created_at);
                            const days = Math.floor(diffMs / (1000 * 60 * 60 * 24));
                            const hours = Math.floor((diffMs % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
                            const minutes = Math.floor((diffMs % (1000 * 60 * 60)) / (1000 * 60));
                            const parts = [days > 1 ? `${days} jours` : days === 1 ? `1 jour` : "", hours > 1 ? `${hours} heures` : hours === 1 ? `1 heure` : "", minutes > 1 ? `${minutes} minutes` : minutes === 1 ? `1 minute` : ""].filter(Boolean);
                            return `Terminé en ${parts.join(" ")}`;
                        })() : `${Math.floor((new Date() - new Date(ticket.created_at)) / (1000 * 60 * 60 * 24))} jours`) : "N/A"}
                    </td>
                </tr>))}
                </tbody>
            </table>)}
        </div>
    </div>);
};

export default TicketsTable;
